/**
 * TaskDetail.js
 *
 * One task, on its own page, at /tasks/:taskId.
 *
 * The board keeps its quick-look drawer for glancing at a card. This is the
 * page you land on when you actually need to work on something: the full
 * brief, everyone involved, the subtask breakdown and the whole progress
 * history rather than just today's line.
 *
 * Because the route carries the task id, this page is linkable - you can
 * paste it into a chat and the other person lands on the same task.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { useAuth } from "../../AuthContext";
import { useTaskApi } from "./taskApi";
import { tokens } from "../../theme";
import {
  DetailRow,
  EMPTY_PROGRESS,
  PERMISSION_LABEL,
  PRIORITIES,
  STAFF_IMAGE_BASE,
  STAGES,
  SectionCard,
  StatCard,
  atLeast,
  buildUi,
  derivedStatus,
  daysBetween,
  fmtDate,
  initials,
  isOverdue,
  num,
  priorityColor as priorityColorFor,
  stageLabel,
  today,
} from "./taskShared";

import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import ArrowBackIosNewOutlinedIcon from "@mui/icons-material/ArrowBackIosNewOutlined";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import CloseIcon from "@mui/icons-material/Close";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import EventRepeatOutlinedIcon from "@mui/icons-material/EventRepeatOutlined";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import HistoryEduOutlinedIcon from "@mui/icons-material/HistoryEduOutlined";
import PlaylistAddCheckOutlinedIcon from "@mui/icons-material/PlaylistAddCheckOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";

const ROLE_LABEL = {
  leader: "Leader",
  supervisor: "Supervisor",
  collaborator: "Collaborator",
};

// The enum on task_members_V2, in the order people think about them.
const ROLES = ["leader", "supervisor", "collaborator"];

const EMPTY_PERSON = { staff_id: "", role_on_task: "collaborator" };

const EMPTY_TASK = {
  title: "",
  description: "",
  department: "",
  visibility: "private",
  priority: "normal",
  start_date: "",
  deadline: "",
  folder_id: "0",
};

const EMPTY_SUBTASK = {
  title: "",
  priority: "normal",
  deadline: "",
  owner_staff_id: "",
};

const avatarSrc = (image) =>
  image ? `${STAFF_IMAGE_BASE}${image}` : undefined;

/**
 * What the browser can render inline. Anything else gets an honest
 * "no preview" panel rather than being handed to an outside service.
 */
const previewKind = (attachment) => {
  const type = String(attachment?.file_type || "").toLowerCase();
  const ext = String(attachment?.file_name || "").split(".").pop().toLowerCase();

  if (type.startsWith("image/") || Number(attachment?.is_image) === 1) return "image";
  if (type === "application/pdf" || ext === "pdf") return "pdf";
  if (type.startsWith("video/") || ["mp4", "webm", "ogg", "mov"].includes(ext))
    return "video";
  if (type.startsWith("audio/") || ["mp3", "wav", "m4a"].includes(ext))
    return "audio";
  if (type.startsWith("text/") || ["txt", "csv", "log", "md"].includes(ext))
    return "text";
  return "none";
};

const fmtBytes = (n) => {
  const size = Number(n) || 0;
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(0)} KB`;
  return `${(size / 1024 / 1024).toFixed(1)} MB`;
};

/** MySQL DATETIME -> Date. Safari refuses the space form. */
const parseStamp = (v) => new Date(String(v || "").replace(" ", "T"));

/** Just the clock, for inside a chat bubble. */
const clock = (v) => {
  const d = parseStamp(v);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
};

/** "Today" / "Yesterday" / "29 Sep 2026", for the day separators. */
const dayLabel = (v) => {
  const d = parseStamp(v);
  if (Number.isNaN(d.getTime())) return "";
  const midnight = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((midnight(new Date()) - midnight(d)) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/**
 * Consecutive messages from one person are grouped the way a chat app does
 * it: the name and avatar appear once at the top of the run, and a day
 * separator is inserted whenever the date changes.
 */
const decorateThread = (comments) =>
  comments.map((c, i) => {
    const prev = comments[i - 1];
    const next = comments[i + 1];
    const newDay = !prev || dayLabel(prev.createDate) !== dayLabel(c.createDate);
    const sameAsPrev =
      prev && String(prev.staff_id) === String(c.staff_id) && !newDay;
    const sameAsNext =
      next &&
      String(next.staff_id) === String(c.staff_id) &&
      dayLabel(next.createDate) === dayLabel(c.createDate);
    return { ...c, newDay, firstOfRun: !sameAsPrev, lastOfRun: !sameAsNext };
  });

/* ───────────────────────── Person row ───────────────────────── */

const PersonRow = ({
  ui,
  name,
  image,
  first,
  last,
  caption,
  chip,
  chipColor,
  action,
}) => (
  <Box display="flex" alignItems="center" gap={1.5} py={1}>
    <Avatar src={avatarSrc(image)} sx={{ width: 38, height: 38 }}>
      {initials(first, last)}
    </Avatar>
    <Box flex={1} minWidth={0}>
      <Typography variant="body2" fontWeight={600} color={ui.textPrimary} noWrap>
        {name || "Unknown"}
      </Typography>
      {caption && (
        <Typography variant="caption" color={ui.textSecondary} noWrap display="block">
          {caption}
        </Typography>
      )}
    </Box>
    {chip && (
      <Chip
        size="small"
        label={chip}
        sx={{
          fontWeight: 600,
          color: chipColor || ui.accent,
          bgcolor: alpha(chipColor || ui.accent, 0.14),
        }}
      />
    )}
    {action}
  </Box>
);

/* ───────────────────────── Page ───────────────────────── */

const TaskDetail = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  const { taskId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { userID } = useAuth();
  const callAction = useTaskApi();
  const myId = String(userID || "");

  const ui = useMemo(() => buildUi(colors, isDark), [colors, isDark]);
  const priorityColor = useCallback((p) => priorityColorFor(colors, p), [colors]);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [detail, setDetail] = useState(null);
  const [saving, setSaving] = useState(false);

  const [progressForm, setProgressForm] = useState({
    ...EMPTY_PROGRESS,
    log_date: today(),
  });

  // Edit the task itself
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(EMPTY_TASK);

  // Add another subtask, or edit one that is already there
  const [subtaskOpen, setSubtaskOpen] = useState(false);
  const [subtaskForm, setSubtaskForm] = useState(EMPTY_SUBTASK);
  const [editingSubtask, setEditingSubtask] = useState(null);

  // Rescheduling an overdue task
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [rescheduleForm, setRescheduleForm] = useState({
    start_date: "",
    deadline: "",
  });

  // Comments thread
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [pendingFile, setPendingFile] = useState(null);
  const [viewing, setViewing] = useState(null);
  const threadEndRef = useRef(null);
  const fileInputRef = useRef(null);

  // Put people on the task, or take them off
  const [personOpen, setPersonOpen] = useState(false);
  const [personForm, setPersonForm] = useState(EMPTY_PERSON);
  const [memberToRemove, setMemberToRemove] = useState(null);
  const [staffs, setStaffs] = useState([]);

  /* ---------------- Load ---------------- */

  const load = useCallback(async () => {
    if (!taskId) return;
    setLoading(true);
    setLoadError("");

    // The account in the URL is the board that sent us here. It decides what
    // you may do with a task that lives in someone else's account but shows
    // on a board you have access to.
    const res = await callAction("proccess.php", "getTaskDetail", {
      id: taskId,
      account_id: searchParams.get("account") || "",
    });

    if (!res.success) {
      setLoadError(res.message || "Could not open this task");
      setDetail(null);
      setLoading(false);
      return;
    }

    setDetail(res.data);
    setLoading(false);
  }, [callAction, taskId, searchParams]);

  useEffect(() => {
    load();
  }, [load]);

  const task = detail?.task || null;
  // "" means no account relationship at all - different from a granted
  // "view", which is a deliberate read-only decision about this account.
  const permission = detail?.permission || "";

  /**
   * What each level may do here:
   *
   *   view  - read only
   *   edit  - edit the task, edit subtasks, comment
   *   full  - also add subtasks, add people, log daily progress
   *
   * Being ON the task overrides all of it for the working actions: your
   * own work is yours to report on, whatever your account permission is.
   */
  const isMember = Number(detail?.is_member) === 1;
  // Membership is a fallback for people with no account grant, not an
  // override - a granted "view only" stays read-only throughout.
  const canWork = atLeast(permission, "edit") || (isMember && !permission);
  const canManage = atLeast(permission, "full") || (isMember && !permission);

  // Rewriting the brief and bringing people in stay with the account.
  const canEditTask = atLeast(permission, "edit");
  const canAssign = atLeast(permission, "full");

  const subtasks = detail?.subtasks || [];
  const members = detail?.members || [];
  const progress = detail?.progress || [];
  const comments = detail?.comments || [];
  const thread = useMemo(() => decorateThread(comments), [comments]);

  // Open the drawer, or post, and you should be looking at the newest message.
  useEffect(() => {
    if (!commentsOpen) return;
    const id = setTimeout(
      () => threadEndRef.current?.scrollIntoView({ block: "end" }),
      60
    );
    return () => clearTimeout(id);
  }, [commentsOpen, comments.length]);

  /* ---------------- Derived ---------------- */

  /**
   * Every subtask is offered here, not just the ones you are accountable
   * for. If someone is off sick their colleagues pick the work up rather
   * than the subtask stalling until they are back - so anyone on the task
   * can report against any part of it.
   */

  // Only guard against a subtask that has since been deleted.
  useEffect(() => {
    if (
      progressForm.subtask_id !== "0" &&
      !subtasks.some((s) => String(s.id) === String(progressForm.subtask_id))
    ) {
      setProgressForm((f) => ({ ...f, subtask_id: "0" }));
    }
  }, [subtasks, progressForm.subtask_id]);

  const myLogToday = useMemo(
    () =>
      progress.find(
        (p) => String(p.staff_id) === myId && p.log_date === today()
      ) || null,
    [progress, myId]
  );

  // Pre-fill the form when today's log already exists, so filing twice edits
  // rather than silently creating a second row.
  useEffect(() => {
    if (myLogToday) {
      setProgressForm({
        subtask_id: String(myLogToday.subtask_id ?? "0"),
        log_date: myLogToday.log_date,
        hours_spent: String(myLogToday.hours_spent ?? "1"),
        progress_percent: String(myLogToday.progress_percent ?? ""),
        work_done: myLogToday.work_done || "",
        blockers: myLogToday.blockers || "",
      });
    } else {
      setProgressForm({ ...EMPTY_PROGRESS, log_date: today() });
    }
  }, [myLogToday]);

  const doneSubtasks = subtasks.filter((s) => s.status === "completed").length;

  /**
   * Stored on the task, but the subtasks are the truth when there are any -
   * and the stored value only catches up when the rollup writes it. Deriving
   * it here means the bar moves as soon as a subtask is ticked.
   */
  const overallPercent = useMemo(() => {
    if (!subtasks.length) return num(task?.progress_percent);
    const total = subtasks.reduce(
      (n, s) => n + (s.status === "completed" ? 100 : num(s.progress_percent)),
      0
    );
    return Math.round(total / subtasks.length);
  }, [subtasks, task]);
  const totalHours = progress.reduce((sum, p) => sum + num(p.hours_spent), 0);
  const daysLeft = task ? daysBetween(today(), task.deadline) : 0;
  const overdue = isOverdue(task);

  /**
   * What the dates say the stage is. Displayed instead of the stored value so
   * the chip can never contradict the deadline printed next to it - the
   * stored one only catches up when the board loads or the cron runs.
   */
  const shownStatus = task ? derivedStatus(task, today()) : "active";

  const backToBoard = () => {
    const account = searchParams.get("account");
    navigate(account ? `/tasks/dashboard?account=${account}` : "/tasks/dashboard");
  };

  /* ---------------- Actions ---------------- */

  const setSubtaskStage = async (subtask, status) => {
    if (!canWork) {
      toast.error("You may only view this task");
      return;
    }
    const res = await callAction("proccess.php", "saveSubtask", {
      id: subtask.id,
      task_id: subtask.task_id,
      status,
    });
    if (!res.success) {
      toast.error(res.message || "Could not update the subtask");
      return;
    }
    toast.success("Subtask updated");
    await load();
  };

  const setStage = async (status) => {
    if (!canWork) {
      toast.error("You may only view this task");
      return;
    }
    const res = await callAction("proccess.php", "moveTask", {
      id: task.id,
      status,
    });
    if (!res.success) {
      // Refused when the task has subtasks - the rollup owns its stage then.
      toast.error(res.message || "Could not change the stage");
      return;
    }
    toast.success(`Marked ${stageLabel(status)}`);
    await load();
  };

  /* ---- Comments ---- */

  const pickFile = (e) => {
    const file = e.target.files?.[0];
    // Same ceiling addData enforces - better to say so before uploading.
    if (file && file.size > 10 * 1024 * 1024) {
      toast.error("That file is larger than 10MB");
    } else if (file) {
      setPendingFile(file);
    }
    // Reset, so choosing the same file twice still fires onChange.
    e.target.value = "";
  };

  const submitComment = async () => {
    const body = commentText.trim();
    if (!body && !pendingFile) return;

    setSaving(true);

    // The comment goes first: the attachment needs its id to sit in the
    // right bubble. A file on its own still gets a comment to hang from.
    const res = await callAction("proccess.php", "saveTaskComment", {
      task_id: task.id,
      comment: body || (pendingFile ? `Attached ${pendingFile.name}` : ""),
    });

    if (!res.success) {
      setSaving(false);
      toast.error(res.message || "Could not post the comment");
      return;
    }

    if (pendingFile) {
      const up = await callAction("proccess.php", "saveTaskAttachment", {
        task_id: task.id,
        comment_id: res.data?.id || "0",
        file: pendingFile,
      });
      if (!up.success) {
        // The comment is already posted, so say what actually happened
        // rather than pretending the whole thing failed.
        toast.error(up.message || "Comment posted, but the file did not upload");
      }
    }

    setSaving(false);
    setCommentText("");
    setPendingFile(null);
    await load();
  };

  /* ---- People on the task ---- */

  /**
   * The staff list is only needed by the add-person picker, so it is fetched
   * when that opens rather than on every page view - it is a whole-table
   * read through the generic CRUD.
   */
  const openAddPerson = useCallback(async () => {
    setPersonOpen(true);
    if (staffs.length) return;
    const res = await callAction("proccess.php", "getDatas", { table: "staffs" });
    if (res.success && Array.isArray(res.data)) setStaffs(res.data);
  }, [callAction, staffs.length]);

  // Active staff who are not already on this task.
  const assignableStaffs = useMemo(() => {
    const taken = new Set(members.map((m) => String(m.staff_id)));
    return staffs
      .filter((s) =>
        ["active", "1"].includes(String(s.status || "").toLowerCase().trim())
      )
      .filter((s) => !taken.has(String(s.Id)))
      .sort((a, b) =>
        `${a.FirstName} ${a.LastName}`.localeCompare(`${b.FirstName} ${b.LastName}`)
      );
  }, [staffs, members]);

  const submitPerson = async () => {
    if (!personForm.staff_id) {
      toast.error("Choose someone to add");
      return;
    }
    setSaving(true);
    const res = await callAction("proccess.php", "saveTaskMember", {
      task_id: task.id,
      staff_id: personForm.staff_id,
      role_on_task: personForm.role_on_task || "collaborator",
    });
    setSaving(false);

    if (!res.success) {
      toast.error(res.message || "Could not add them to the task");
      return;
    }
    toast.success("Added to the task");
    setPersonForm(EMPTY_PERSON);
    setPersonOpen(false);
    await load();
  };

  const confirmRemovePerson = async () => {
    const person = memberToRemove;
    setMemberToRemove(null);
    if (!person) return;

    const res = await callAction("proccess.php", "removeTaskMember", {
      task_id: task.id,
      staff_id: person.staff_id,
    });
    if (!res.success) {
      toast.error(res.message || "Could not remove them");
      return;
    }
    toast.success(`${person.staff_name || "They"} removed from the task`);
    await load();
  };

  /* ---- Reschedule an overdue task ---- */

  const openReschedule = () => {
    setRescheduleForm({
      start_date: task.start_date || today(),
      deadline: task.deadline || today(),
    });
    setRescheduleOpen(true);
  };

  /**
   * Where the new dates will put it. Shown live in the dialog, so the person
   * can see the consequence before saving rather than being surprised by it.
   */
  const rescheduleLands = useMemo(
    () =>
      derivedStatus(
        {
          status: "active",
          start_date: rescheduleForm.start_date,
          deadline: rescheduleForm.deadline,
        },
        today()
      ),
    [rescheduleForm]
  );

  const submitReschedule = async () => {
    if (!rescheduleForm.start_date || !rescheduleForm.deadline) {
      toast.error("Both dates are required");
      return;
    }
    if (rescheduleForm.deadline < rescheduleForm.start_date) {
      toast.error("The deadline cannot be before the start date");
      return;
    }

    setSaving(true);
    // saveTask re-derives the stage from the dates and re-files the card, so
    // there is no stage to pick here.
    const res = await callAction("proccess.php", "saveTask", {
      id: task.id,
      owner_id: detail.owner_id,
      start_date: rescheduleForm.start_date,
      deadline: rescheduleForm.deadline,
    });
    setSaving(false);

    if (!res.success) {
      toast.error(res.message || "Could not reschedule the task");
      return;
    }
    toast.success(`Rescheduled - now ${stageLabel(rescheduleLands).toLowerCase()}`);
    setRescheduleOpen(false);
    await load();
  };

  /* ---- Edit the task ---- */

  const openEdit = () => {
    setEditForm({
      title: task.title || "",
      description: task.description || "",
      department: task.department || "",
      visibility: task.visibility || "private",
      priority: task.priority || "normal",
      start_date: task.start_date || "",
      deadline: task.deadline || "",
      folder_id: String(task.folder_id ?? "0"),
    });
    setEditOpen(true);
  };

  const submitEdit = async () => {
    if (!editForm.title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!editForm.start_date || !editForm.deadline) {
      toast.error("Both dates are required");
      return;
    }
    if (editForm.deadline < editForm.start_date) {
      toast.error("The deadline cannot be before the start date");
      return;
    }

    setSaving(true);
    const res = await callAction("proccess.php", "saveTask", {
      ...editForm,
      id: task.id,
      owner_id: detail.owner_id,
    });
    setSaving(false);

    if (!res.success) {
      toast.error(res.message || "Could not save the task");
      return;
    }
    toast.success("Task updated");
    setEditOpen(false);
    await load();
  };

  /* ---- Add a subtask ---- */

  const openSubtaskForm = (subtask) => {
    setEditingSubtask(subtask || null);
    setSubtaskForm(
      subtask
        ? {
            title: subtask.title || "",
            priority: subtask.priority || "normal",
            deadline: subtask.deadline || "",
            owner_staff_id: String(subtask.owner_staff_id || ""),
          }
        : EMPTY_SUBTASK
    );
    setSubtaskOpen(true);
  };

  const closeSubtaskForm = () => {
    setSubtaskOpen(false);
    setEditingSubtask(null);
    setSubtaskForm(EMPTY_SUBTASK);
  };

  const submitSubtask = async () => {
    if (!subtaskForm.title.trim()) {
      toast.error("Give the subtask a title");
      return;
    }

    setSaving(true);
    const res = await callAction("proccess.php", "saveSubtask", {
      task_id: task.id,
      title: subtaskForm.title.trim(),
      priority: subtaskForm.priority || "normal",
      deadline: subtaskForm.deadline || "",
      owner_staff_id: subtaskForm.owner_staff_id || "",
      ...(editingSubtask
        ? { id: editingSubtask.id }
        : // New rows go to the end of the list.
          { sort_order: subtasks.length }),
    });
    setSaving(false);

    if (!res.success) {
      toast.error(
        res.message ||
          (editingSubtask ? "Could not save the subtask" : "Could not add the subtask")
      );
      return;
    }
    toast.success(editingSubtask ? "Subtask updated" : "Subtask added");
    closeSubtaskForm();
    await load();
  };

  const submitProgress = async () => {
    if (!progressForm.work_done.trim()) {
      toast.error("Describe what you worked on");
      return;
    }
    setSaving(true);
    const res = await callAction("proccess.php", "logTaskProgress", {
      task_id: task.id,
      subtask_id: progressForm.subtask_id || "0",
      log_date: progressForm.log_date || today(),
      hours_spent: progressForm.hours_spent || "0",
      progress_percent: progressForm.progress_percent,
      work_done: progressForm.work_done.trim(),
      blockers: progressForm.blockers.trim(),
    });
    setSaving(false);

    if (!res.success) {
      toast.error(res.message || "Could not save your progress");
      return;
    }
    toast.success(myLogToday ? "Progress updated" : "Progress recorded");
    await load();
  };

  /* ---------------- Shared field style ---------------- */

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      bgcolor: ui.surfaceAlt,
      borderRadius: "10px",
      color: ui.textPrimary,
      "& fieldset": { borderColor: ui.border },
      "&:hover fieldset": { borderColor: ui.accent },
      "&.Mui-focused fieldset": { borderColor: ui.accent },
    },
    "& .MuiInputLabel-root": { color: ui.textSecondary },
    "& .MuiInputLabel-root.Mui-focused": { color: ui.accent },
  };

  /* ---------------- States ---------------- */

  if (loading) {
    return (
      <Box
        minHeight="60vh"
        display="grid"
        sx={{ placeItems: "center", bgcolor: ui.pageBg }}
      >
        <Stack alignItems="center" gap={2}>
          <CircularProgress sx={{ color: ui.accent }} />
          <Typography color={ui.textSecondary}>Opening task…</Typography>
        </Stack>
      </Box>
    );
  }

  if (loadError || !task) {
    return (
      <Box p={{ xs: 2, md: 4 }} sx={{ bgcolor: ui.pageBg, minHeight: "100%" }}>
        <SectionCard ui={ui} title="This task could not be opened">
          <Typography color={ui.textSecondary} mb={3}>
            {loadError || "The task no longer exists."}
          </Typography>
          <Button
            onClick={backToBoard}
            startIcon={<ArrowBackIosNewOutlinedIcon />}
            sx={{
              background: ui.gradient,
              color: "#fff",
              px: 3,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Back to the board
          </Button>
        </SectionCard>
        <ToastContainer position="bottom-right" theme={isDark ? "dark" : "light"} />
      </Box>
    );
  }

  /* ---------------- Page ---------------- */

  return (
    <Box p={{ xs: 2, md: 4 }} sx={{ bgcolor: ui.pageBg, minHeight: "100%" }}>
      {/* Header */}
      <Box
        sx={{
          borderRadius: "16px",
          p: { xs: 2.5, md: 3.5 },
          mb: 3,
          background: ui.gradient,
          boxShadow: ui.shadow,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Stack direction="row" alignItems="center" gap={1.5} mb={1.5}>
          <Tooltip title="Back to the board">
            <IconButton
              onClick={backToBoard}
              size="small"
              sx={{
                color: "#fff",
                bgcolor: alpha("#000", 0.16),
                "&:hover": { bgcolor: alpha("#000", 0.28) },
              }}
            >
              <ArrowBackIosNewOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Typography
            variant="caption"
            sx={{ color: alpha("#fff", 0.85), fontWeight: 700, letterSpacing: 1 }}
          >
            {task.task_code}
          </Typography>
          {String(task.visibility) === "public" ? (
            <Chip
              size="small"
              icon={<PublicOutlinedIcon sx={{ color: "#fff !important" }} />}
              label="Public"
              sx={{ color: "#fff", bgcolor: alpha("#000", 0.2), fontWeight: 600 }}
            />
          ) : (
            <Chip
              size="small"
              icon={<LockOutlinedIcon sx={{ color: "#fff !important" }} />}
              label="Private"
              sx={{ color: "#fff", bgcolor: alpha("#000", 0.2), fontWeight: 600 }}
            />
          )}
        </Stack>

        <Typography variant="h3" fontWeight={800} color="#fff" mb={1}>
          {task.title}
        </Typography>

        <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
          <Chip
            size="small"
            label={task.priority}
            sx={{
              textTransform: "capitalize",
              fontWeight: 700,
              color: "#fff",
              bgcolor: alpha(priorityColor(task.priority), 0.9),
            }}
          />
          <Chip
            size="small"
            label={stageLabel(shownStatus)}
            sx={{ color: "#fff", bgcolor: alpha("#000", 0.2), fontWeight: 600 }}
          />
          {detail.folder && (
            <Chip
              size="small"
              icon={<FolderOutlinedIcon sx={{ color: "#fff !important" }} />}
              label={detail.folder.name}
              sx={{ color: "#fff", bgcolor: alpha("#000", 0.2), fontWeight: 600 }}
            />
          )}
          {overdue && (
            <Chip
              size="small"
              icon={<ReportProblemOutlinedIcon sx={{ color: "#fff !important" }} />}
              label={`Overdue by ${Math.abs(daysLeft)} day${
                Math.abs(daysLeft) === 1 ? "" : "s"
              }`}
              sx={{ color: "#fff", bgcolor: alpha(colors.red[500], 0.9), fontWeight: 700 }}
            />
          )}
          <Box flex={1} />
          <Chip
            size="small"
            label={
              permission
                ? PERMISSION_LABEL[permission] || permission
                : "Assigned to you"
            }
            sx={{ color: "#fff", bgcolor: alpha("#000", 0.2), fontWeight: 600 }}
          />
          <Button
            size="small"
            startIcon={<ChatBubbleOutlineOutlinedIcon />}
            onClick={() => setCommentsOpen(true)}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "10px",
              color: "#fff",
              bgcolor: alpha("#000", 0.24),
              "&:hover": { bgcolor: alpha("#000", 0.36) },
            }}
          >
            Comments{comments.length ? ` (${comments.length})` : ""}
          </Button>
          <Tooltip title={canEditTask ? "" : "You may only view this task"}>
            <span>
              <Button
                size="small"
                startIcon={<EditNoteOutlinedIcon />}
                onClick={openEdit}
                disabled={!canEditTask}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: "10px",
                  color: "#fff",
                  bgcolor: alpha("#000", 0.24),
                  "&:hover": { bgcolor: alpha("#000", 0.36) },
                  "&.Mui-disabled": { color: alpha("#fff", 0.5) },
                }}
              >
                Edit task
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      {/* The one thing that needs saying before anything else on the page. */}
      {overdue && (
        <Box
          sx={{
            mb: 3,
            p: { xs: 2, md: 2.5 },
            borderRadius: "14px",
            display: "flex",
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            bgcolor: alpha(colors.red[500], 0.1),
            border: `1px solid ${alpha(colors.red[500], 0.45)}`,
          }}
        >
          <ReportProblemOutlinedIcon sx={{ color: colors.red[500], fontSize: 30 }} />

          <Box flex={1} minWidth={0}>
            <Typography variant="h6" fontWeight={700} color={ui.textPrimary}>
              This task is overdue
            </Typography>
            <Typography variant="body2" color={ui.textSecondary}>
              The deadline was {fmtDate(task.deadline)} —{" "}
              {Math.abs(daysLeft)} day{Math.abs(daysLeft) === 1 ? "" : "s"} ago.
              {canEditTask
                ? " Give it new dates and it moves back to To do or In progress."
                : ""}
            </Typography>
          </Box>

          <Tooltip title={canEditTask ? "" : "You may only view this task"}>
            <span>
              <Button
                startIcon={<EventRepeatOutlinedIcon />}
                onClick={openReschedule}
                disabled={!canEditTask}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: "10px",
                  px: 2.5,
                  color: "#fff",
                  bgcolor: colors.red[500],
                  whiteSpace: "nowrap",
                  "&:hover": { bgcolor: colors.red[600] || colors.red[500] },
                  "&.Mui-disabled": { opacity: 0.5, color: "#fff" },
                }}
              >
                Reschedule
              </Button>
            </span>
          </Tooltip>
        </Box>
      )}

      {/* Stats */}
      <Grid container spacing={2} mb={1}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            ui={ui}
            icon={<PlaylistAddCheckOutlinedIcon />}
            label="Subtasks done"
            value={subtasks.length ? `${doneSubtasks}/${subtasks.length}` : "—"}
            sub={subtasks.length ? "Drives the task stage" : "No subtasks yet"}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            ui={ui}
            icon={<EventOutlinedIcon />}
            label={overdue ? "Overdue by" : "Days remaining"}
            value={Math.abs(daysLeft)}
            sub={fmtDate(task.deadline)}
            tone={overdue ? colors.red[500] : undefined}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            ui={ui}
            icon={<HistoryEduOutlinedIcon />}
            label="Hours logged"
            value={totalHours.toFixed(1)}
            sub={`${progress.length} entr${progress.length === 1 ? "y" : "ies"}`}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            ui={ui}
            icon={<PeopleAltOutlinedIcon />}
            label="People on it"
            value={members.length}
            sub={detail.owner_name ? `Account: ${detail.owner_name}` : ""}
          />
        </Grid>
      </Grid>

      <Box mt={3}>
        <Grid container spacing={3}>
          {/* Left column */}
          <Grid size={{ xs: 12, lg: 8 }}>
            <SectionCard ui={ui} title="The brief">
              <DetailRow ui={ui} label="Description" value={task.description} />
              <DetailRow ui={ui} label="Department" value={task.department} />
              <DetailRow
                ui={ui}
                label="Dates"
                value={`${fmtDate(task.start_date)} → ${fmtDate(task.deadline)}`}
                color={overdue ? colors.red[500] : undefined}
              />
              <DetailRow
                ui={ui}
                label="Raised by"
                value={`${detail.author_name || "Unknown"}${
                  task.origin === "staff" ? " (staff request)" : " (management)"
                }`}
              />
              <DetailRow ui={ui} label="Filed in" value={detail.folder?.name || "Unfiled"} />
              <DetailRow ui={ui} label="Created" value={fmtDate(task.createDate)} />
              {task.completed_at && (
                <DetailRow ui={ui} label="Completed" value={fmtDate(task.completed_at)} />
              )}

              <Box mt={2.5}>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography variant="caption" color={ui.textSecondary} fontWeight={600}>
                    Overall progress
                  </Typography>
                  <Typography variant="caption" color={ui.textPrimary} fontWeight={700}>
                    {overallPercent}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, overallPercent)}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    bgcolor: alpha(ui.accent, 0.15),
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 4,
                      background: ui.gradient,
                    },
                  }}
                />
              </Box>

              {/* Stage control - only when the rollup is not driving it */}
              {canWork && subtasks.length === 0 && (
                <Box mt={3}>
                  <Typography variant="caption" color={ui.textSecondary} fontWeight={600}>
                    Move this task
                  </Typography>
                  <Stack direction="row" gap={1} mt={1} flexWrap="wrap">
                    {/* Overdue is not offered: it is set by the deadline, not
                        chosen. On hold is, because pausing is a decision. */}
                    {STAGES.filter((s) => s.value !== "overdue").map((s) => (
                      <Button
                        key={s.value}
                        size="small"
                        onClick={() => setStage(s.value)}
                        disabled={shownStatus === s.value}
                        sx={{
                          textTransform: "none",
                          borderRadius: "10px",
                          fontWeight: 600,
                          px: 2,
                          color: shownStatus === s.value ? "#fff" : ui.accent,
                          background:
                            shownStatus === s.value ? ui.gradient : "transparent",
                          border: `1px solid ${
                            shownStatus === s.value ? "transparent" : ui.border
                          }`,
                        }}
                      >
                        {s.label}
                      </Button>
                    ))}
                  </Stack>
                </Box>
              )}
              {subtasks.length > 0 && (
                <Typography variant="caption" color={ui.textSecondary} display="block" mt={2}>
                  The stage follows the subtasks automatically — tick them off below.
                </Typography>
              )}
            </SectionCard>

            {/* Subtasks */}
            <SectionCard
              ui={ui}
              title="Subtasks"
              hint={
                subtasks.length
                  ? "Completing every subtask moves the task to Completed."
                  : undefined
              }
              action={
                <Tooltip
                  title={canManage ? "" : "You need full rights to add subtasks"}
                >
                  <span>
                    <Button
                      size="small"
                      startIcon={<AddCircleOutlineOutlinedIcon />}
                      onClick={() =>
                        subtaskOpen ? closeSubtaskForm() : openSubtaskForm(null)
                      }
                      disabled={!canManage}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        borderRadius: "10px",
                        color: ui.accent,
                        border: `1px solid ${ui.border}`,
                        px: 1.5,
                      }}
                    >
                      {subtaskOpen ? "Cancel" : "Add subtask"}
                    </Button>
                  </span>
                </Tooltip>
              }
            >
              {subtaskOpen && (
                <Box
                  sx={{
                    border: `1px solid ${ui.border}`,
                    borderRadius: "12px",
                    p: 2,
                    mb: 2,
                    bgcolor: ui.surfaceAlt,
                  }}
                >
                  <Stack gap={1.5}>
                    <TextField
                      size="small"
                      fullWidth
                      autoFocus
                      label="What needs doing?"
                      value={subtaskForm.title}
                      onChange={(e) =>
                        setSubtaskForm((f) => ({ ...f, title: e.target.value }))
                      }
                      sx={fieldSx}
                    />
                    <Stack direction={{ xs: "column", sm: "row" }} gap={1.5}>
                      <TextField
                        select
                        size="small"
                        label="Priority"
                        value={subtaskForm.priority}
                        onChange={(e) =>
                          setSubtaskForm((f) => ({ ...f, priority: e.target.value }))
                        }
                        sx={{ ...fieldSx, minWidth: 130 }}
                      >
                        {PRIORITIES.map((p) => (
                          <MenuItem key={p.value} value={p.value}>
                            {p.label}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        size="small"
                        type="date"
                        label="Deadline"
                        InputLabelProps={{ shrink: true }}
                        value={subtaskForm.deadline}
                        onChange={(e) =>
                          setSubtaskForm((f) => ({ ...f, deadline: e.target.value }))
                        }
                        sx={{ ...fieldSx, minWidth: 170 }}
                      />
                      {/* Only people already on the task: a subtask cannot
                          be handed to someone who is not working on it. */}
                      <TextField
                        select
                        size="small"
                        label="Accountable"
                        value={subtaskForm.owner_staff_id}
                        disabled={members.length === 0}
                        helperText={
                          members.length === 0
                            ? "Assign someone to the task first"
                            : undefined
                        }
                        onChange={(e) =>
                          setSubtaskForm((f) => ({
                            ...f,
                            owner_staff_id: e.target.value,
                          }))
                        }
                        sx={{ ...fieldSx, flex: 1, minWidth: 180 }}
                      >
                        <MenuItem value="">Nobody yet</MenuItem>
                        {members.map((m) => (
                          <MenuItem key={m.id} value={String(m.staff_id)}>
                            {m.staff_name || "Unknown"}
                            {m.role_on_task
                              ? ` — ${ROLE_LABEL[m.role_on_task] || m.role_on_task}`
                              : ""}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Stack>
                    <Box>
                      <Button
                        onClick={submitSubtask}
                        disabled={saving}
                        sx={{
                          background: ui.gradient,
                          color: "#fff",
                          borderRadius: "10px",
                          textTransform: "none",
                          fontWeight: 600,
                          px: 3,
                          "&.Mui-disabled": { opacity: 0.6, color: "#fff" },
                        }}
                      >
                        {saving
                          ? "Saving…"
                          : editingSubtask
                          ? "Save changes"
                          : "Add subtask"}
                      </Button>
                      <Button
                        onClick={closeSubtaskForm}
                        sx={{
                          ml: 1,
                          textTransform: "none",
                          color: ui.textSecondary,
                        }}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Stack>
                </Box>
              )}

              {subtasks.length === 0 ? (
                <Typography color={ui.textSecondary} variant="body2">
                  No subtasks. This task is tracked as a single piece of work.
                </Typography>
              ) : (
                <Stack divider={<Divider sx={{ borderColor: alpha(ui.border, 0.6) }} />}>
                  {subtasks.map((s) => {
                    const done = s.status === "completed";
                    return (
                      <Box key={s.id} display="flex" alignItems="center" gap={1.5} py={1.2}>
                        <Tooltip title={done ? "Mark not done" : "Mark done"}>
                          <span>
                            <IconButton
                              size="small"
                              disabled={!canWork}
                              onClick={() =>
                                setSubtaskStage(s, done ? "inprogress" : "completed")
                              }
                              sx={{ color: done ? colors.greenAccent?.[500] || ui.accent : ui.textSecondary }}
                            >
                              {done ? (
                                <CheckCircleIcon fontSize="small" />
                              ) : (
                                <RadioButtonUncheckedIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>

                        <Box flex={1} minWidth={0}>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            color={ui.textPrimary}
                            sx={{ textDecoration: done ? "line-through" : "none" }}
                          >
                            {s.title}
                          </Typography>
                          <Typography variant="caption" color={ui.textSecondary}>
                            {s.owner_name ? `${s.owner_name} · ` : ""}
                            {s.deadline ? `due ${fmtDate(s.deadline)}` : "no deadline"}
                          </Typography>
                        </Box>

                        <Chip
                          size="small"
                          label={s.priority}
                          sx={{
                            textTransform: "capitalize",
                            fontWeight: 600,
                            color: priorityColor(s.priority),
                            bgcolor: alpha(priorityColor(s.priority), 0.16),
                          }}
                        />

                        {canWork && (
                          <Tooltip title="Edit subtask">
                            <IconButton
                              size="small"
                              onClick={() => openSubtaskForm(s)}
                              sx={{ color: ui.textSecondary }}
                            >
                              <EditNoteOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </SectionCard>

            {/* Progress history */}
            <SectionCard
              ui={ui}
              title="Progress history"
              hint="Every daily log filed against this task, newest first."
            >
              {progress.length === 0 ? (
                <Typography color={ui.textSecondary} variant="body2">
                  Nothing logged yet.
                </Typography>
              ) : (
                <Stack gap={1.5}>
                  {progress.map((p) => (
                    <Box
                      key={p.id}
                      sx={{
                        border: `1px solid ${ui.border}`,
                        borderRadius: "12px",
                        p: 1.8,
                        bgcolor: ui.surfaceAlt,
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1.2} mb={1}>
                        <Avatar src={avatarSrc(p.staff_image)} sx={{ width: 30, height: 30 }}>
                          {initials(p.staff_first, p.staff_last)}
                        </Avatar>
                        <Typography variant="body2" fontWeight={600} color={ui.textPrimary}>
                          {p.staff_name || "Unknown"}
                        </Typography>
                        <Box flex={1} />
                        <Typography variant="caption" color={ui.textSecondary}>
                          {fmtDate(p.log_date)} · {num(p.hours_spent)}h
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        color={ui.textPrimary}
                        sx={{ whiteSpace: "pre-wrap" }}
                      >
                        {p.work_done}
                      </Typography>
                      {p.blockers && (
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            mt: 1,
                            color: colors.red[400] || colors.red[500],
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          Blocker: {p.blockers}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Stack>
              )}
            </SectionCard>
          </Grid>

          {/* Right column */}
          <Grid size={{ xs: 12, lg: 4 }}>
            <SectionCard
              ui={ui}
              title="People"
              action={
                <Tooltip
                  title={canAssign ? "" : "You need full rights to assign people"}
                >
                  <span>
                    <Button
                      size="small"
                      startIcon={<PersonAddAltOutlinedIcon />}
                      onClick={() =>
                        personOpen ? setPersonOpen(false) : openAddPerson()
                      }
                      disabled={!canAssign}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        borderRadius: "10px",
                        color: ui.accent,
                        border: `1px solid ${ui.border}`,
                        px: 1.5,
                      }}
                    >
                      {personOpen ? "Cancel" : "Add person"}
                    </Button>
                  </span>
                </Tooltip>
              }
            >
              {personOpen && (
                <Box
                  sx={{
                    border: `1px solid ${ui.border}`,
                    borderRadius: "12px",
                    p: 2,
                    mb: 2,
                    bgcolor: ui.surfaceAlt,
                  }}
                >
                  <Stack gap={1.5}>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      label="Who"
                      value={personForm.staff_id}
                      disabled={assignableStaffs.length === 0}
                      helperText={
                        assignableStaffs.length === 0
                          ? "Everyone active is already on this task"
                          : undefined
                      }
                      onChange={(e) =>
                        setPersonForm((f) => ({ ...f, staff_id: e.target.value }))
                      }
                      sx={fieldSx}
                    >
                      {assignableStaffs.map((s) => (
                        <MenuItem key={s.Id} value={String(s.Id)}>
                          {`${s.FirstName || ""} ${s.LastName || ""}`.trim()}
                          {s.department ? ` — ${s.department}` : ""}
                        </MenuItem>
                      ))}
                    </TextField>

                    <TextField
                      select
                      size="small"
                      fullWidth
                      label="Role on this task"
                      value={personForm.role_on_task}
                      onChange={(e) =>
                        setPersonForm((f) => ({
                          ...f,
                          role_on_task: e.target.value,
                        }))
                      }
                      sx={fieldSx}
                    >
                      {ROLES.map((r) => (
                        <MenuItem key={r} value={r}>
                          {ROLE_LABEL[r]}
                        </MenuItem>
                      ))}
                    </TextField>

                    <Box>
                      <Button
                        onClick={submitPerson}
                        disabled={saving || !personForm.staff_id}
                        sx={{
                          background: ui.gradient,
                          color: "#fff",
                          borderRadius: "10px",
                          textTransform: "none",
                          fontWeight: 600,
                          px: 3,
                          "&.Mui-disabled": { opacity: 0.6, color: "#fff" },
                        }}
                      >
                        {saving ? "Adding…" : "Add to task"}
                      </Button>
                    </Box>
                  </Stack>
                </Box>
              )}

              {members.length === 0 ? (
                <Typography color={ui.textSecondary} variant="body2">
                  Nobody is assigned yet.
                </Typography>
              ) : (
                <Stack divider={<Divider sx={{ borderColor: alpha(ui.border, 0.6) }} />}>
                  {members.map((m) => (
                    <PersonRow
                      key={m.id}
                      ui={ui}
                      name={m.staff_name}
                      image={m.staff_image}
                      first={m.staff_first}
                      last={m.staff_last}
                      caption={m.staff_department}
                      chip={ROLE_LABEL[m.role_on_task] || m.role_on_task}
                      action={
                        canAssign ? (
                          <Tooltip title="Remove from task">
                            <IconButton
                              size="small"
                              onClick={() => setMemberToRemove(m)}
                              sx={{ color: ui.textSecondary }}
                            >
                              <PersonRemoveOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : null
                      }
                    />
                  ))}
                </Stack>
              )}
            </SectionCard>

            {/* Daily progress - the thing staff come here to do */}
            <SectionCard
              ui={ui}
              title={myLogToday ? "Edit today's progress" : "Log today's progress"}
              hint={
                canManage
                  ? "Daily progress is mandatory on tasks in progress."
                  : "Only people working on this task record progress against it."
              }
            >
              <Stack gap={2} sx={{ opacity: canManage ? 1 : 0.55 }}>
                {subtasks.length > 0 && (
                  <TextField
                    disabled={!canManage}
                    select
                    size="small"
                    fullWidth
                    label="Against"
                    value={progressForm.subtask_id}
                    onChange={(e) =>
                      setProgressForm((f) => ({ ...f, subtask_id: e.target.value }))
                    }
                    sx={fieldSx}
                  >
                    <MenuItem value="0">The task as a whole</MenuItem>
                    {subtasks.map((s) => (
                      <MenuItem key={s.id} value={String(s.id)}>
                        {s.title}
                        {s.owner_name && String(s.owner_staff_id) !== myId
                          ? ` — ${s.owner_name}`
                          : ""}
                      </MenuItem>
                    ))}
                  </TextField>
                )}

                <Stack direction="row" gap={1.5}>
                  <TextField
                    size="small"
                    type="date"
                    label="Date"
                    disabled={!canManage}
                    InputLabelProps={{ shrink: true }}
                    value={progressForm.log_date}
                    onChange={(e) =>
                      setProgressForm((f) => ({ ...f, log_date: e.target.value }))
                    }
                    sx={{ ...fieldSx, flex: 1 }}
                  />
                  <TextField
                    size="small"
                    type="number"
                    label="Hours"
                    disabled={!canManage}
                    inputProps={{ step: "0.5", min: "0" }}
                    value={progressForm.hours_spent}
                    onChange={(e) =>
                      setProgressForm((f) => ({ ...f, hours_spent: e.target.value }))
                    }
                    sx={{ ...fieldSx, width: 110 }}
                  />
                </Stack>

                <TextField
                  size="small"
                  fullWidth
                  multiline
                  minRows={3}
                  label="What did you work on?"
                  disabled={!canManage}
                  value={progressForm.work_done}
                  onChange={(e) =>
                    setProgressForm((f) => ({ ...f, work_done: e.target.value }))
                  }
                  sx={fieldSx}
                />

                <TextField
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  label="Anything blocking you?"
                  disabled={!canManage}
                  value={progressForm.blockers}
                  onChange={(e) =>
                    setProgressForm((f) => ({ ...f, blockers: e.target.value }))
                  }
                  sx={fieldSx}
                />

                <Button
                  onClick={submitProgress}
                  disabled={saving || !canManage}
                  sx={{
                    background: ui.gradient,
                    color: "#fff",
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: 600,
                    py: 1,
                    "&.Mui-disabled": { opacity: 0.6, color: "#fff" },
                  }}
                >
                  {saving
                    ? "Saving…"
                    : myLogToday
                    ? "Update my progress"
                    : "Record my progress"}
                </Button>
              </Stack>
            </SectionCard>
          </Grid>
        </Grid>
      </Box>

      {/* ---------------- Edit the task ---------------- */}
      <Dialog
        open={editOpen}
        onClose={(e, reason) => {
          // Not on a stray click outside - people lose a half-typed brief.
          if (reason === "backdropClick") return;
          setEditOpen(false);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: ui.surface,
            borderRadius: "16px",
            border: `1px solid ${ui.border}`,
          },
        }}
      >
        <DialogTitle sx={{ color: ui.textPrimary, fontWeight: 700 }}>
          Edit {task.task_code}
          <IconButton
            onClick={() => setEditOpen(false)}
            sx={{ position: "absolute", right: 12, top: 12, color: ui.textSecondary }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider sx={{ borderColor: ui.border }} />

        <DialogContent sx={{ pt: 3 }}>
          <Stack gap={2.2}>
            <TextField
              size="small"
              fullWidth
              label="Title"
              value={editForm.title}
              onChange={(e) =>
                setEditForm((f) => ({ ...f, title: e.target.value }))
              }
              sx={fieldSx}
            />
            <TextField
              size="small"
              fullWidth
              multiline
              minRows={3}
              label="Description"
              value={editForm.description}
              onChange={(e) =>
                setEditForm((f) => ({ ...f, description: e.target.value }))
              }
              sx={fieldSx}
            />
            <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
              <TextField
                size="small"
                fullWidth
                label="Department"
                value={editForm.department}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, department: e.target.value }))
                }
                sx={fieldSx}
              />
              <TextField
                select
                size="small"
                fullWidth
                label="Priority"
                value={editForm.priority}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, priority: e.target.value }))
                }
                sx={fieldSx}
              >
                {PRIORITIES.map((p) => (
                  <MenuItem key={p.value} value={p.value}>
                    {p.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
              <TextField
                size="small"
                fullWidth
                type="date"
                label="Start date"
                InputLabelProps={{ shrink: true }}
                value={editForm.start_date}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, start_date: e.target.value }))
                }
                sx={fieldSx}
              />
              <TextField
                size="small"
                fullWidth
                type="date"
                label="Deadline"
                InputLabelProps={{ shrink: true }}
                value={editForm.deadline}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, deadline: e.target.value }))
                }
                sx={fieldSx}
              />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
              <TextField
                select
                size="small"
                fullWidth
                label="Folder"
                value={editForm.folder_id}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, folder_id: e.target.value }))
                }
                sx={fieldSx}
              >
                <MenuItem value="0">Unfiled</MenuItem>
                {(detail.folders || []).map((f) => (
                  <MenuItem key={f.id} value={String(f.id)}>
                    {f.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                fullWidth
                label="Visibility"
                value={editForm.visibility}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, visibility: e.target.value }))
                }
                sx={fieldSx}
              >
                <MenuItem value="private">Private</MenuItem>
                <MenuItem value="public">Public</MenuItem>
              </TextField>
            </Stack>
          </Stack>
        </DialogContent>

        <Divider sx={{ borderColor: ui.border }} />
        <DialogActions sx={{ px: 3, py: 2.5 }}>
          <Button
            onClick={() => setEditOpen(false)}
            sx={{ textTransform: "none", color: ui.textSecondary }}
          >
            Cancel
          </Button>
          <Button
            onClick={submitEdit}
            disabled={saving}
            sx={{
              background: ui.gradient,
              color: "#fff",
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              "&.Mui-disabled": { opacity: 0.6, color: "#fff" },
            }}
          >
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------------- Comments ---------------- */}
      <Drawer
        anchor="right"
        open={commentsOpen}
        onClose={() => setCommentsOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 420 },
            bgcolor: ui.surface,
            borderLeft: `1px solid ${ui.border}`,
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2.5,
            background: ui.gradient,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <ChatBubbleOutlineOutlinedIcon sx={{ color: "#fff" }} />
          <Box flex={1} minWidth={0}>
            <Typography variant="h6" fontWeight={700} color="#fff" noWrap>
              Comments
            </Typography>
            <Typography variant="caption" sx={{ color: alpha("#fff", 0.85) }} noWrap>
              {task.task_code} · {task.title}
            </Typography>
          </Box>
          <IconButton onClick={() => setCommentsOpen(false)} sx={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Thread */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: 1.5,
            py: 2,
            bgcolor: ui.surfaceAlt,
          }}
        >
          {comments.length === 0 ? (
            <Box textAlign="center" py={6}>
              <ChatBubbleOutlineOutlinedIcon
                sx={{ fontSize: 42, color: alpha(ui.textSecondary, 0.5) }}
              />
              <Typography variant="body2" color={ui.textSecondary} mt={1}>
                No comments yet. Start the conversation.
              </Typography>
            </Box>
          ) : (
            <Box>
              {thread.map((c) => {
                const mine = String(c.staff_id) === myId;
                return (
                  <Box key={c.id}>
                    {c.newDay && (
                      <Box display="flex" justifyContent="center" my={1.5}>
                        <Box
                          sx={{
                            px: 1.5,
                            py: 0.4,
                            borderRadius: "8px",
                            bgcolor: alpha(ui.textSecondary, 0.16),
                          }}
                        >
                          <Typography
                            variant="caption"
                            fontWeight={600}
                            color={ui.textSecondary}
                          >
                            {dayLabel(c.createDate)}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    <Box
                      display="flex"
                      justifyContent={mine ? "flex-end" : "flex-start"}
                      alignItems="flex-end"
                      gap={0.8}
                      mb={c.lastOfRun ? 1.2 : 0.3}
                    >
                      {/* Their avatar sits once, at the end of the run. */}
                      {!mine && (
                        <Box sx={{ width: 28 }}>
                          {c.lastOfRun && (
                            <Avatar
                              src={avatarSrc(c.staff_image)}
                              sx={{ width: 28, height: 28 }}
                            >
                              {initials(c.staff_first, c.staff_last)}
                            </Avatar>
                          )}
                        </Box>
                      )}

                      <Box
                        sx={{
                          maxWidth: "78%",
                          minWidth: 96,
                          px: 1.4,
                          pt: 0.8,
                          pb: 0.5,
                          bgcolor: mine
                            ? alpha(ui.accent, isDark ? 0.34 : 0.18)
                            : ui.surface,
                          border: mine ? "none" : `1px solid ${ui.border}`,
                          // The squared corner is the bubble's tail, and it
                          // only appears on the last message of a run.
                          borderRadius: "12px",
                          ...(c.lastOfRun && mine
                            ? { borderBottomRightRadius: "3px" }
                            : {}),
                          ...(c.lastOfRun && !mine
                            ? { borderBottomLeftRadius: "3px" }
                            : {}),
                          boxShadow: isDark ? "none" : "0 1px 1px rgba(0,0,0,0.06)",
                        }}
                      >
                        {!mine && c.firstOfRun && (
                          <Typography
                            variant="caption"
                            fontWeight={700}
                            sx={{ color: ui.accent2, display: "block", mb: 0.2 }}
                          >
                            {c.staff_name || "Unknown"}
                          </Typography>
                        )}

                        {(c.attachments || []).map((a) =>
                          Number(a.is_image) === 1 ? (
                            <Box
                              key={a.id}
                              onClick={() => setViewing(a)}
                              sx={{ display: "block", mb: 0.6, cursor: "pointer" }}
                            >
                              <Box
                                component="img"
                                src={a.url}
                                alt={a.file_name}
                                sx={{
                                  display: "block",
                                  maxWidth: "100%",
                                  maxHeight: 220,
                                  borderRadius: "8px",
                                }}
                              />
                            </Box>
                          ) : (
                            <Box
                              key={a.id}
                              onClick={() => setViewing(a)}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 0.6,
                                p: 1,
                                borderRadius: "8px",
                                cursor: "pointer",
                                bgcolor: alpha(ui.textSecondary, 0.12),
                                "&:hover": {
                                  bgcolor: alpha(ui.textSecondary, 0.2),
                                },
                              }}
                            >
                              <InsertDriveFileOutlinedIcon
                                sx={{ fontSize: 22, color: ui.accent }}
                              />
                              <Box minWidth={0}>
                                <Typography
                                  variant="caption"
                                  fontWeight={600}
                                  color={ui.textPrimary}
                                  noWrap
                                  display="block"
                                >
                                  {a.file_name}
                                </Typography>
                                <Typography variant="caption" color={ui.textSecondary}>
                                  {fmtBytes(a.file_size)}
                                </Typography>
                              </Box>
                            </Box>
                          )
                        )}

                        <Typography
                          variant="body2"
                          color={ui.textPrimary}
                          sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                        >
                          {c.comment}
                        </Typography>

                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            textAlign: "right",
                            mt: 0.2,
                            fontSize: "0.65rem",
                            color: alpha(ui.textSecondary, 0.9),
                          }}
                        >
                          {clock(c.createDate)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
              <div ref={threadEndRef} />
            </Box>
          )}
        </Box>

        {/* Composer */}
        <Box sx={{ p: 2, borderTop: `1px solid ${ui.border}` }}>
          {pendingFile && (
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              mb={1}
              sx={{
                border: `1px solid ${ui.border}`,
                borderRadius: "10px",
                px: 1.2,
                py: 0.8,
                bgcolor: ui.surfaceAlt,
              }}
            >
              <AttachFileOutlinedIcon sx={{ fontSize: 18, color: ui.accent }} />
              <Box flex={1} minWidth={0}>
                <Typography variant="caption" fontWeight={600} color={ui.textPrimary} noWrap display="block">
                  {pendingFile.name}
                </Typography>
                <Typography variant="caption" color={ui.textSecondary}>
                  {fmtBytes(pendingFile.size)}
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => setPendingFile(null)}>
                <CloseIcon sx={{ fontSize: 16, color: ui.textSecondary }} />
              </IconButton>
            </Box>
          )}

          <TextField
            size="small"
            fullWidth
            multiline
            minRows={2}
            maxRows={6}
            placeholder={
              canWork ? "Write a comment…" : "You may only view this task"
            }
            disabled={!canWork}
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            onKeyDown={(e) => {
              // Enter sends, Shift+Enter makes a new line.
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submitComment();
              }
            }}
            sx={fieldSx}
          />

          <Box display="flex" alignItems="center" gap={1} mt={1}>
            <input
              ref={fileInputRef}
              type="file"
              hidden
              onChange={pickFile}
            />
            <Tooltip title={canWork ? "Attach a file" : "You may only view this task"}>
              <span>
                <IconButton
                  size="small"
                  disabled={!canWork || saving}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{ color: ui.textSecondary }}
                >
                  <AttachFileOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Typography variant="caption" color={ui.textSecondary} flex={1}>
              Enter to send · Shift+Enter for a new line
            </Typography>
            <Button
              onClick={submitComment}
              disabled={
                saving || !canWork || (!commentText.trim() && !pendingFile)
              }
              startIcon={<SendOutlinedIcon />}
              sx={{
                background: ui.gradient,
                color: "#fff",
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 600,
                px: 2.5,
                "&.Mui-disabled": { opacity: 0.5, color: "#fff" },
              }}
            >
              Send
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* ---------------- Reschedule ---------------- */}
      <Dialog
        open={rescheduleOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick") return;
          setRescheduleOpen(false);
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: ui.surface,
            borderRadius: "16px",
            border: `1px solid ${ui.border}`,
          },
        }}
      >
        <DialogTitle sx={{ color: ui.textPrimary, fontWeight: 700 }}>
          Reschedule {task.task_code}
        </DialogTitle>
        <Divider sx={{ borderColor: ui.border }} />

        <DialogContent sx={{ pt: 3 }}>
          <Stack gap={2.2}>
            <TextField
              size="small"
              fullWidth
              type="date"
              label="New start date"
              InputLabelProps={{ shrink: true }}
              value={rescheduleForm.start_date}
              onChange={(e) =>
                setRescheduleForm((f) => ({ ...f, start_date: e.target.value }))
              }
              sx={fieldSx}
            />
            <TextField
              size="small"
              fullWidth
              type="date"
              label="New deadline"
              InputLabelProps={{ shrink: true }}
              value={rescheduleForm.deadline}
              onChange={(e) =>
                setRescheduleForm((f) => ({ ...f, deadline: e.target.value }))
              }
              sx={fieldSx}
            />

            <Box
              sx={{
                p: 1.5,
                borderRadius: "10px",
                bgcolor: alpha(ui.accent, 0.08),
                border: `1px dashed ${alpha(ui.accent, 0.5)}`,
              }}
            >
              <Typography variant="caption" color={ui.textSecondary} display="block">
                Where it will go
              </Typography>
              <Typography variant="body2" fontWeight={700} color={ui.textPrimary}>
                {stageLabel(rescheduleLands)}
              </Typography>
              <Typography variant="caption" color={ui.textSecondary}>
                {rescheduleLands === "active"
                  ? "Starting later, so it waits in To do."
                  : rescheduleLands === "overdue"
                  ? "That deadline is still in the past, so it stays overdue."
                  : "Already started and not yet due, so it is in progress."}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>

        <Divider sx={{ borderColor: ui.border }} />
        <DialogActions sx={{ px: 3, py: 2.5 }}>
          <Button
            onClick={() => setRescheduleOpen(false)}
            sx={{ textTransform: "none", color: ui.textSecondary }}
          >
            Cancel
          </Button>
          <Button
            onClick={submitReschedule}
            disabled={saving}
            sx={{
              background: ui.gradient,
              color: "#fff",
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              "&.Mui-disabled": { opacity: 0.6, color: "#fff" },
            }}
          >
            {saving ? "Saving…" : "Save new dates"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------------- Attachment viewer ---------------- */}
      <Dialog
        open={!!viewing}
        onClose={() => setViewing(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: ui.surface,
            borderRadius: "16px",
            border: `1px solid ${ui.border}`,
            height: { xs: "100%", md: "88vh" },
            m: { xs: 0, md: 4 },
          },
        }}
      >
        {viewing && (
          <>
            <Box
              sx={{
                p: 2,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                borderBottom: `1px solid ${ui.border}`,
              }}
            >
              <InsertDriveFileOutlinedIcon sx={{ color: ui.accent }} />
              <Box flex={1} minWidth={0}>
                <Typography
                  variant="body2"
                  fontWeight={700}
                  color={ui.textPrimary}
                  noWrap
                >
                  {viewing.file_name}
                </Typography>
                <Typography variant="caption" color={ui.textSecondary}>
                  {fmtBytes(viewing.file_size)}
                  {viewing.file_type ? ` · ${viewing.file_type}` : ""}
                </Typography>
              </Box>

              <Tooltip title="Download">
                <IconButton
                  component="a"
                  href={viewing.url}
                  download={viewing.file_name}
                  sx={{ color: ui.textSecondary }}
                >
                  <DownloadOutlinedIcon />
                </IconButton>
              </Tooltip>
              <IconButton
                onClick={() => setViewing(null)}
                sx={{ color: ui.textSecondary }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                bgcolor: ui.surfaceAlt,
                display: "grid",
                placeItems: "center",
                overflow: "auto",
                p: previewKind(viewing) === "image" ? 2 : 0,
              }}
            >
              {previewKind(viewing) === "image" && (
                <Box
                  component="img"
                  src={viewing.url}
                  alt={viewing.file_name}
                  sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                />
              )}

              {["pdf", "text"].includes(previewKind(viewing)) && (
                <Box
                  component="iframe"
                  title={viewing.file_name}
                  src={viewing.url}
                  sx={{ width: "100%", height: "100%", border: "none" }}
                />
              )}

              {previewKind(viewing) === "video" && (
                <Box
                  component="video"
                  src={viewing.url}
                  controls
                  sx={{ maxWidth: "100%", maxHeight: "100%" }}
                />
              )}

              {previewKind(viewing) === "audio" && (
                <Box component="audio" src={viewing.url} controls sx={{ width: "80%" }} />
              )}

              {previewKind(viewing) === "none" && (
                <Box textAlign="center" p={4}>
                  <InsertDriveFileOutlinedIcon
                    sx={{ fontSize: 56, color: alpha(ui.textSecondary, 0.5) }}
                  />
                  <Typography variant="body2" color={ui.textPrimary} fontWeight={600} mt={1}>
                    This file type cannot be previewed here
                  </Typography>
                  <Typography variant="caption" color={ui.textSecondary} display="block" mb={2}>
                    Word, Excel and PowerPoint files need to be opened in their
                    own application.
                  </Typography>
                  <Button
                    component="a"
                    href={viewing.url}
                    download={viewing.file_name}
                    startIcon={<DownloadOutlinedIcon />}
                    sx={{
                      background: ui.gradient,
                      color: "#fff",
                      borderRadius: "10px",
                      textTransform: "none",
                      fontWeight: 600,
                      px: 3,
                    }}
                  >
                    Download
                  </Button>
                </Box>
              )}
            </Box>
          </>
        )}
      </Dialog>

      {/* ---------------- Remove someone from the task ---------------- */}
      <Dialog
        open={!!memberToRemove}
        onClose={() => setMemberToRemove(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: ui.surface,
            borderRadius: "16px",
            border: `1px solid ${ui.border}`,
          },
        }}
      >
        <DialogTitle sx={{ color: ui.textPrimary, fontWeight: 700 }}>
          Remove from this task?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color={ui.textSecondary}>
            {memberToRemove?.staff_name || "This person"} will no longer be on{" "}
            <strong>{task.task_code}</strong>. Progress they have already logged
            stays on the record.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5 }}>
          <Button
            onClick={() => setMemberToRemove(null)}
            sx={{ textTransform: "none", color: ui.textSecondary }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmRemovePerson}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "10px",
              px: 3,
              color: colors.red[500],
              border: `1px solid ${alpha(colors.red[500], 0.5)}`,
            }}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      <ToastContainer position="bottom-right" theme={isDark ? "dark" : "light"} />
    </Box>
  );
};

export default TaskDetail;
